/// <reference types="vite/client" />

import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import React from 'react'
import { AuthShell } from './AuthShell'
import { apiPost } from '../../lib/api'
import { AUTH_RESEND_CODE, AUTH_VERIFY_EMAIL } from '../../lib/endpoints'
import { colors } from '../../config/themeAuth'

type ApiResponse = {
  status: number
  message: string
}

export default function VerifyEmail() {
  const navigate = useNavigate()
  const [params] = useSearchParams()

  const initialEmail = params.get('email') ?? ''
  const initialCode = params.get('verificationCode') ?? ''
  const [email, setEmail] = React.useState(initialEmail)
  const [otp, setOtp] = React.useState(() =>
    initialCode.length === 6
      ? initialCode.split('')
      : ['', '', '', '', '', '']
  )
  const [loading, setLoading] = React.useState(false)
  const [resendLoading, setResendLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [success, setSuccess] = React.useState<string | null>(null)
  const [verified, setVerified] = React.useState(false)
  const [resendCountdown, setResendCountdown] = React.useState(60)
  const inputsRef = React.useRef<Array<HTMLInputElement | null>>([])

  const code = otp.join('')
  const canVerify = React.useMemo(() => email.trim().length > 3 && code.length === 6, [email, code])
  const canResend = resendCountdown === 0 && !resendLoading && email.trim().length >= 4

  React.useEffect(() => {
    if (initialEmail && initialCode.length === 6) {
      setLoading(true)
      apiPost<ApiResponse, { email: string; code: string }>(AUTH_VERIFY_EMAIL, {
        email: initialEmail.trim(),
        code: initialCode.trim(),
      })
        .then(() => setVerified(true))
        .catch(err => {
          setError(err instanceof Error ? err.message : 'Verification failed')
        })
        .finally(() => setLoading(false))
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  React.useEffect(() => {
    if (resendCountdown <= 0) return
    const intervalId = window.setInterval(() => {
      setResendCountdown(prev => (prev <= 1 ? 0 : prev - 1))
    }, 1000)
    return () => window.clearInterval(intervalId)
  }, [resendCountdown])

  React.useEffect(() => {
    if (!error && !success) return
    const timeoutId = window.setTimeout(() => {
      setError(null)
      setSuccess(null)
    }, 10000)
    return () => window.clearTimeout(timeoutId)
  }, [error, success])

  function updateOtpAt(index: number, value: string) {
    if (!/^\d?$/.test(value)) return
    setOtp(prev => {
      const next = [...prev]
      next[index] = value
      return next
    })
  }

  function handleOtpChange(index: number, value: string) {
    const digit = value.replace(/\D/g, '').slice(-1)
    updateOtpAt(index, digit)
    if (digit && index < 5) {
      inputsRef.current[index + 1]?.focus()
    }
  }

  function handleOtpKeyDown(index: number, e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputsRef.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowLeft' && index > 0) {
      e.preventDefault()
      inputsRef.current[index - 1]?.focus()
    }
    if (e.key === 'ArrowRight' && index < 5) {
      e.preventDefault()
      inputsRef.current[index + 1]?.focus()
    }
  }

  function handleOtpPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6)
    if (!pasted) return
    e.preventDefault()
    const next = ['', '', '', '', '', '']
    pasted.split('').forEach((char, idx) => {
      next[idx] = char
    })
    setOtp(next)
    inputsRef.current[Math.min(pasted.length, 6) - 1]?.focus()
  }

  async function handleVerify(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    setSuccess(null)
    setLoading(true)
    try {
      await apiPost<ApiResponse, { email: string; code: string }>(
        AUTH_VERIFY_EMAIL,
        {
          email: email.trim(),
          code: code.trim(),
        }
      )
      setVerified(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed')
    } finally {
      setLoading(false)
    }
  }

  async function handleResend() {
    if (!canResend) return
    setError(null)
    setSuccess(null)
    setResendLoading(true)
    try {
      await apiPost<ApiResponse, { email: string }>(AUTH_RESEND_CODE, {
        email: email.trim(),
      })
      setSuccess('A new code has been sent to your email.')
      setResendCountdown(60)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not resend code')
    } finally {
      setResendLoading(false)
    }
  }

  React.useEffect(() => {
    if (!verified) return
    const id = window.setTimeout(() => navigate('/signin', { replace: true }), 10000)
    return () => window.clearTimeout(id)
  }, [verified, navigate])

  if (verified) {
    return (
      <AuthShell title="You're verified!" subtitle="Your email has been confirmed successfully.">
        <div className="flex flex-col items-center gap-6 py-4 text-center">
          <div
            className="flex h-16 w-16 items-center justify-center rounded-full text-3xl"
            style={{ backgroundColor: colors.sand, color: colors.orange }}
          >
            ✓
          </div>
          <p className="text-sm" style={{ color: colors.muted }}>
            A confirmation email has been sent to <strong style={{ color: colors.ink }}>{email}</strong>.
            You'll be redirected to sign in shortly.
          </p>
          <button
            onClick={() => navigate('/signin', { replace: true })}
            className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all"
            style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
          >
            Go to sign in
          </button>
        </div>
      </AuthShell>
    )
  }

  return (
    <AuthShell
      title="Verify your email"
      subtitle="Enter the code we sent you to activate your account."
    >
      <form onSubmit={handleVerify} className="space-y-4">
        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Email
          </label>
          <input
            value={email}
            onChange={e => setEmail(e.target.value)}
            className="nudge-input mt-2 w-full rounded-2xl border px-4 py-3 text-sm"
            style={{ borderColor: colors.border, backgroundColor: colors.inputBg }}
            type="email"
            autoComplete="email"
            required
          />
        </div>

        <div>
          <label className="text-sm font-medium" style={{ color: colors.ink }}>
            Verification code
          </label>
          <div className="mt-2 flex items-center justify-between gap-2">
            {otp.map((digit, index) => (
              <input
                key={index}
                ref={el => {
                  inputsRef.current[index] = el
                }}
                value={digit}
                onChange={e => handleOtpChange(index, e.target.value)}
                onKeyDown={e => handleOtpKeyDown(index, e)}
                onPaste={handleOtpPaste}
                className="nudge-input h-14 w-12 rounded-xl border text-center text-xl font-semibold"
                style={{
                  borderColor: digit ? colors.orange : colors.border,
                  backgroundColor: colors.inputBg,
                  color: colors.ink,
                }}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={1}
                aria-label={`Code digit ${index + 1}`}
                required
              />
            ))}
          </div>
          <p className="mt-2 text-xs" style={{ color: colors.muted }}>
            Enter the 6-digit code. You can also paste it.
          </p>
        </div>

        {error || success ? (
          <div
            className="rounded-xl border px-3.5 py-2.5 text-sm animate-[fadeIn_.18s_ease]"
            style={{
              borderColor: colors.border,
              backgroundColor: colors.sand,
              color: error ? colors.darkOrange : colors.ink,
            }}
            role="status"
            aria-live="polite"
          >
            {error ?? success}
          </div>
        ) : null}

        <button
          type="submit"
          disabled={!canVerify || loading}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold transition-all disabled:opacity-60"
          style={{ backgroundColor: colors.ctaBg, color: colors.ctaColor }}
        >
          {loading ? 'Verifying…' : 'Verify email'}
        </button>

        <button
          type="button"
          onClick={handleResend}
          disabled={!canResend}
          className="w-full rounded-2xl px-4 py-3 text-sm font-semibold border transition-all disabled:opacity-60 inline-flex items-center justify-center gap-2"
          style={{
            borderColor: colors.border,
            color: colors.ink,
            backgroundColor: colors.surface,
          }}
        >
          {resendLoading ? (
            <>
              <span
                className="h-4 w-4 rounded-full border-2 border-current border-t-transparent animate-spin"
                aria-hidden
              />
              Sending...
            </>
          ) : resendCountdown > 0 ? (
            `Resend in ${resendCountdown}s`
          ) : (
            'Resend code'
          )}
        </button>
      </form>

      <p className="mt-6 text-sm" style={{ color: colors.muted }}>
        Ready?{' '}
        <Link to="/signin" className="font-semibold underline" style={{ color: colors.ink }}>
          Back to sign in
        </Link>
      </p>
    </AuthShell>
  )
}
